// src/lib/push.ts

import { supabase, supabaseUrl as SUPABASE_URL, supabaseAnonKey as SUPABASE_ANON_KEY } from './supabase'
import { Capacitor } from '@capacitor/core'
import { FirebaseMessaging } from '@capacitor-firebase/messaging'

async function callEdgeFunction(body: object): Promise<void> {
  try {
    const { data: { session } } = await supabase.auth.getSession()
    const bearerToken = session?.access_token ?? SUPABASE_ANON_KEY
    await fetch(`${SUPABASE_URL}/functions/v1/send-notification`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${bearerToken}`,
        'apikey': SUPABASE_ANON_KEY,
      },
      body: JSON.stringify(body)
    })
  } catch (err) {
    console.error('Edge function call failed:', err)
  }
}

// Native (iOS/Android) push via Firebase Cloud Messaging. The device returns an
// FCM token that the existing send-notification function already delivers to, so
// the same backend serves web and native — only how we obtain the token differs.
async function saveNativeToken(token: string): Promise<void> {
  const { data: { session } } = await supabase.auth.getSession()
  const bearerToken = session?.access_token ?? SUPABASE_ANON_KEY
  await fetch(`${SUPABASE_URL}/functions/v1/send-notification`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${bearerToken}`,
      'apikey': SUPABASE_ANON_KEY,
    },
    body: JSON.stringify({ action: 'save-token', token }),
  })
}

// iOS may not have the APNs token wired to Firebase in the instant after the
// user grants permission; retry a few times so the first enable still succeeds.
async function getNativeTokenWithRetry(attempts = 4, delayMs = 1500): Promise<string | null> {
  for (let i = 0; i < attempts; i++) {
    try {
      const { token } = await FirebaseMessaging.getToken()
      if (token) return token
    } catch (err) {
      if (i === attempts - 1) throw err
    }
    await new Promise(resolve => setTimeout(resolve, delayMs))
  }
  return null
}

// Captures why the last native enable attempt failed, so the UI can show the
// real reason instead of silently flipping the toggle back off.
let lastNativePushError: string | null = null
export function getLastPushError(): string | null { return lastNativePushError }

async function registerNativePush(userId: string): Promise<boolean> {
  lastNativePushError = null
  try {
    const perm = await FirebaseMessaging.requestPermissions()
    if (perm.receive !== 'granted') { lastNativePushError = `Permission not granted (${perm.receive})`; return false }
    const token = await getNativeTokenWithRetry()
    if (!token) { lastNativePushError = 'No device token — iOS did not return an APNs token'; return false }
    await saveNativeToken(token)
    await supabase.from('profiles').update({ push_enabled: true }).eq('id', userId)
    return true
  } catch (err: any) {
    lastNativePushError = err?.message ? String(err.message) : String(err)
    console.error('Native push registration failed:', err)
    return false
  }
}

// Called once per session on native so a device that already granted push keeps
// a fresh token saved (FCM tokens can rotate between launches / reinstalls).
let nativePushInited = false
export async function initNativePush(userId: string): Promise<void> {
  if (!Capacitor.isNativePlatform() || nativePushInited) return
  nativePushInited = true
  try {
    FirebaseMessaging.addListener('tokenReceived', (event: { token: string }) => {
      if (event?.token) saveNativeToken(event.token).catch(() => { /* best effort */ })
    })
    const { receive } = await FirebaseMessaging.checkPermissions()
    if (receive === 'granted') {
      const token = await getNativeTokenWithRetry()
      if (token) {
        await saveNativeToken(token)
        await supabase.from('profiles').update({ push_enabled: true }).eq('id', userId)
      }
    }
  } catch (err) {
    console.error('initNativePush failed:', err)
  }
}

export async function registerPushNotifications(userId: string): Promise<boolean> {
  // Native app: real device push (APNs on iOS / FCM on Android). Returns true
  // once the token is saved, so the in-app prompt can show success immediately.
  if (Capacitor.isNativePlatform()) {
    return registerNativePush(userId)
  }
  // Website: Firebase Messaging must run in a standalone page (crashes React's event loop).
  // Open as popup so app state is preserved.
  const url = `/enable-notifications.html?uid=${encodeURIComponent(userId)}`
  const popup = window.open(url, 'sb-push-setup', 'width=480,height=360,menubar=no,toolbar=no,location=no')
  if (!popup) {
    // Popup blocked — fall back to full navigation
    window.location.href = url
  }
  return false
}

export async function unregisterPushNotifications(userId: string): Promise<void> {
  try {
    // Native: drop the device's FCM token so this device stops receiving pushes.
    if (Capacitor.isNativePlatform()) {
      try { await FirebaseMessaging.deleteToken() } catch { /* best effort */ }
    }
    // Device token lives in the owner-only user_push table; push_enabled stays on profiles.
    await supabase
      .from('user_push')
      .update({ push_token: null, push_subscription: null, updated_at: new Date().toISOString() })
      .eq('user_id', userId)
    await supabase
      .from('profiles')
      .update({ push_enabled: false })
      .eq('id', userId)
  } catch (err) {
    console.error('Push unregister failed:', err)
  }
}

export async function sendPushToUser(targetUserId: string, title: string, body: string, data?: Record<string, string>): Promise<void> {
  try { await callEdgeFunction({ targetUserId, title, body, data }) } catch (err) { console.error('sendPushToUser failed:', err) }
}

export async function sendBroadcastNotification(title: string, body: string, data?: Record<string, string>): Promise<void> {
  try { await callEdgeFunction({ broadcast: true, title, body, data }) } catch (err) { console.error('sendBroadcastNotification failed:', err) }
}

export async function notifyLike(postOwnerId: string, likerUsername: string, ratingId?: string) {
  if (!postOwnerId) return
  const data: Record<string, string> = { type: 'like', tag: 'like' }
  if (ratingId) data.rating_id = ratingId
  await sendPushToUser(postOwnerId, 'Someone liked your brew ☕', `${likerUsername} liked your post`, data)
}

export async function notifyComment(postOwnerId: string, commenterUsername: string, commentPreview: string, ratingId?: string) {
  if (!postOwnerId) return
  const data: Record<string, string> = { type: 'comment', tag: 'comment' }
  if (ratingId) data.rating_id = ratingId
  await sendPushToUser(postOwnerId, `${commenterUsername} commented`, commentPreview.slice(0, 100), data)
}

export async function notifyFollow(followedUserId: string, followerUsername: string, actorId?: string) {
  if (!followedUserId) return
  const data: Record<string, string> = { type: 'follow', tag: 'follow' }
  if (actorId) data.actor_id = actorId
  await sendPushToUser(followedUserId, 'New follower!', `${followerUsername} started following you`, data)
}

export async function notifyMention(mentionedUserId: string, mentionerUsername: string, context: string, ratingId?: string) {
  if (!mentionedUserId) return
  const data: Record<string, string> = { type: 'mention', tag: 'mention' }
  if (ratingId) data.rating_id = ratingId
  await sendPushToUser(mentionedUserId, `${mentionerUsername} mentioned you`, context.slice(0, 100), data)
}

export async function notifyNewPost(followerIds: string[], posterUsername: string, shopName: string, isQuickSip = false, isVibe = false, ratingId?: string) {
  const title = isVibe
    ? `${posterUsername} posted a vibe ✨`
    : isQuickSip
    ? `${posterUsername} had a quick sip ⚡`
    : `${posterUsername} rated a visit ☕`
  const body = shopName || 'Check it out on Social Brew'
  const data: Record<string, string> = { type: 'new_post', tag: 'new_post' }
  if (ratingId) data.rating_id = ratingId
  await Promise.all(followerIds.map(id =>
    sendPushToUser(id, title, body, data)
  ))
}

export async function notifyDM(toUserId: string, fromUsername: string, messagePreview: string, actorId?: string) {
  if (!toUserId) return
  const data: Record<string, string> = { type: 'dm', tag: 'dm' }
  if (actorId) data.actor_id = actorId
  await sendPushToUser(toUserId, `${fromUsername} sent you a message ☕`, messagePreview.slice(0, 100), data)
}
